/**
 * Pin Layer Configuration
 *
 * Phase 3: Custom Pin System
 *
 * Provides custom pin markers at GPS coordinates with customizable icons,
 * hover tooltips, and navigation support for the MapComponent.
 *
 * Features:
 * - Custom pins at GPS coordinates
 * - Multiple icon types (location, flag, star, sensor, warning, info)
 * - Configurable colors and scales
 * - Optional text labels
 * - Group-based filtering
 * - Navigation support (targetPageId)
 *
 * @see https://maplibre.org/maplibre-style-spec/layers/#symbol
 */

import type {
  SymbolLayerSpecification,
  ExpressionSpecification,
} from 'maplibre-gl';
import type {
  MapPin,
  MapPinFeature,
  MapPinGeoJSON,
} from '../MapComponent.types';
import type { PinIconName } from '../mapUtils/iconUtils';
import { normalizeColorForImageId } from '../mapUtils/iconUtils';

// ============================================================================
// Layer IDs
// ============================================================================

export const PIN_SOURCE_ID = 'custom-pins';
export const PIN_LAYER_ID = 'custom-pin-icons';
export const PIN_LABELS_LAYER_ID = 'custom-pin-labels';
export const PIN_NAV_INDICATOR_LAYER_ID = 'custom-pin-nav-indicator';

// ============================================================================
// Icon Name Mapping
// ============================================================================

/**
 * Maps MapPinStyle icon types to iconUtils PinIconName
 *
 * The MapPinStyle uses semantic names (location, sensor, warning),
 * while iconUtils uses Lucide icon names (pin, temperature, alert).
 */
const PIN_ICON_TYPE_MAP: Record<NonNullable<MapPin['style']['icon']>, PinIconName> = {
  location: 'pin',
  camera: 'camera',
  sensor: 'sensor',
  power: 'power',
  home: 'home',
  building: 'building',
  warehouse: 'warehouse',
  water: 'water',
  waves: 'waves',
  rain: 'rain',
  flask: 'flask',
  temperature: 'temperature',
  wind: 'wind',
  sun: 'sun',
  wifi: 'wifi',
  radio: 'radio',
  flame: 'flame',
  warning: 'alert',
  maintenance: 'maintenance',
  gauge: 'gauge',
  info: 'info',
  custom: 'pin', // Fallback for custom icons
};

/**
 * Map a MapPinStyle icon type to the corresponding iconUtils PinIconName
 *
 * @param icon - The MapPinStyle icon type
 * @returns The corresponding PinIconName for iconUtils
 */
export const mapPinIconToUtilName = (
  icon: MapPin['style']['icon'] | undefined
): PinIconName => {
  if (!icon) return 'pin';
  return PIN_ICON_TYPE_MAP[icon] || 'pin';
};

// ============================================================================
// GeoJSON Conversion
// ============================================================================

/**
 * Convert pins to GeoJSON FeatureCollection
 *
 * Filters pins by visibility and optional group membership.
 *
 * @param pins - Array of map pins
 * @param visibleGroups - Optional set of visible group IDs for filtering
 * @returns GeoJSON FeatureCollection for pins
 */
export const convertPinsToGeoJSON = (
  pins: MapPin[],
  visibleGroups?: Set<string>,
  selectedPinId?: string,
  hoveredPinId?: string
): MapPinGeoJSON => {
  // Filter visible pins
  let visiblePins = pins.filter((pin) => pin.visible !== false);

  // If group filtering is active, filter by group membership
  if (visibleGroups !== undefined) {
    // Only filter if the pin has a groupId
    visiblePins = visiblePins.filter((pin) => {
      // Pins without a groupId are always visible (ungrouped)
      if (!pin.groupId) return true;
      // Pins with a groupId must be in a visible group
      return visibleGroups.has(pin.groupId);
    });
  }

  const features: MapPinFeature[] = visiblePins.map((pin) => {
    const iconName = mapPinIconToUtilName(pin.style.icon);
    const color = pin.style.color || '#3B82F6';
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [pin.longitude, pin.latitude],
      },
      properties: {
        id: pin.id,
        name: pin.name,
        color,
        icon: iconName,
        scale: pin.style.scale ?? 1,
        label: pin.style.label,
        hasNavigation: !!pin.navigation?.targetPageId,
        targetPageId: pin.navigation?.targetPageId,
        selected: pin.id === selectedPinId,
        hovered: pin.id === hoveredPinId,
        imageId: `pin-marker-${iconName}-${normalizeColorForImageId(color)}`,
      },
    };
  });

  return {
    type: 'FeatureCollection',
    features,
  };
};

// ============================================================================
// Symbol Layout Configuration
// ============================================================================

/**
 * Get pin symbol layer layout configuration
 *
 * Configures icon placement and sizing based on pin properties.
 *
 * @returns MapLibre symbol layout specification
 */
export const getPinSymbolLayout = (): SymbolLayerSpecification['layout'] => ({
  // Use pre-rendered teardrop marker image keyed by icon + color
  'icon-image': ['get', 'imageId'] as ExpressionSpecification,

  // Scale icons based on the scale property, slightly larger when selected/hovered
  'icon-size': [
    '*',
    ['case',
      ['get', 'selected'], 1.3,
      ['get', 'hovered'], 1.2,
      1.0],
    [
      'interpolate',
      ['linear'],
      ['get', 'scale'],
      0.5, 0.6,
      1.0, 0.85,
      1.5, 1.05,
      2.0, 1.3,
    ],
  ] as ExpressionSpecification,

  // Allow overlap for pins (they're important markers)
  'icon-allow-overlap': true,
  'icon-ignore-placement': false,

  // Anchor icon at bottom (pin tip at coordinate)
  'icon-anchor': 'bottom',

  // Rotate with the map
  'icon-rotation-alignment': 'map',
  'icon-pitch-alignment': 'map',
});

/**
 * Get pin symbol layer paint configuration
 *
 * Non-SDF composite images — color is baked in, so no icon-color/halo needed.
 * Hover/selected feedback is handled by icon-size scaling in layout.
 *
 * @returns MapLibre symbol paint specification
 */
export const getPinSymbolPaint = (): SymbolLayerSpecification['paint'] => ({
  'icon-opacity': 1,
});


// ============================================================================
// Label Layout Configuration
// ============================================================================

/**
 * Get pin label layer layout configuration
 *
 * Positions labels below pins and configures text styling.
 *
 * @returns MapLibre symbol layout specification
 */
export const getPinLabelLayout = (): SymbolLayerSpecification['layout'] => ({
  // Show label from properties (only if exists)
  'text-field': ['get', 'label'] as ExpressionSpecification,

  // Label font
  'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],

  // Label size
  'text-size': 11,

  // Position label below pin
  'text-anchor': 'top',
  'text-offset': [0, 0.8],

  // Don't overlap with icons
  'text-allow-overlap': false,
  'text-ignore-placement': false,

  // Limit label width
  'text-max-width': 10,
});

/**
 * Get pin label layer paint configuration
 *
 * @returns MapLibre symbol paint specification
 */
export const getPinLabelPaint = (): SymbolLayerSpecification['paint'] => ({
  // Label text color (dark gray for readability)
  'text-color': '#1F2937',

  // White halo for contrast
  'text-halo-color': 'rgba(255, 255, 255, 0.95)',
  'text-halo-width': 1.5,

  // Full opacity
  'text-opacity': 1,
});

// ============================================================================
// Navigation Indicator Layer Configuration
// ============================================================================

/**
 * Get navigation indicator layout configuration
 *
 * Shows a small navigation badge (arrow icon) on pins with navigation configured.
 * The badge appears in the top-right corner of the pin.
 *
 * @returns MapLibre symbol layout specification
 */
export const getPinNavIndicatorLayout = (): SymbolLayerSpecification['layout'] => ({
  // Show navigation symbol (unicode arrow or link character)
  // Using a simple text character for the navigation indicator
  'text-field': [
    'case',
    ['get', 'hasNavigation'],
    '↗', // Arrow symbol for navigable pins
    '',  // Empty for non-navigable
  ] as ExpressionSpecification,

  // Font settings
  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  'text-size': 10,

  // Position in top-right of pin icon
  'text-anchor': 'bottom-left',
  'text-offset': [1.0, -2.2], // Offset to position at top-right of pin

  // Always show indicator above pin
  'text-allow-overlap': true,
  'text-ignore-placement': true,

  // Only show for pins with navigation
  'visibility': 'visible',
});

/**
 * Get navigation indicator paint configuration
 *
 * Styles the navigation badge with a green color and white background.
 *
 * @returns MapLibre symbol paint specification
 */
export const getPinNavIndicatorPaint = (): SymbolLayerSpecification['paint'] => ({
  // Green color for navigation indicator
  'text-color': '#FFFFFF',

  // Green background halo
  'text-halo-color': '#10B981', // Emerald green
  'text-halo-width': 4,
  'text-halo-blur': 0,

  // Full opacity
  'text-opacity': [
    'case',
    ['get', 'hasNavigation'],
    1,
    0,
  ] as ExpressionSpecification,
});

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the list of icon names needed for pin rendering
 *
 * Used for preloading all required icons on map load.
 *
 * @returns Array of PinIconName values to preload
 */
export const getRequiredPinIcons = (): PinIconName[] => {
  // Return all the iconUtils names we map to
  return ['pin', 'camera', 'sensor', 'power', 'home', 'building', 'warehouse', 'water', 'waves', 'rain', 'flask', 'temperature', 'wind', 'sun', 'wifi', 'radio', 'flame', 'alert', 'maintenance', 'gauge', 'info'];
};

/**
 * Find a pin by its ID
 *
 * @param pins - Array of map pins
 * @param pinId - The pin ID to find
 * @returns The pin if found, undefined otherwise
 */
export const findPinById = (
  pins: MapPin[],
  pinId: string
): MapPin | undefined => {
  return pins.find((pin) => pin.id === pinId);
};

// ============================================================================
// Default Export
// ============================================================================

export default {
  convertPinsToGeoJSON,
  mapPinIconToUtilName,
  getPinSymbolLayout,
  getPinSymbolPaint,
  getPinLabelLayout,
  getPinLabelPaint,
  getPinNavIndicatorLayout,
  getPinNavIndicatorPaint,
  getRequiredPinIcons,
  findPinById,
  PIN_SOURCE_ID,
  PIN_LAYER_ID,
  PIN_LABELS_LAYER_ID,
  PIN_NAV_INDICATOR_LAYER_ID,
};
